import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js/pure';
import { useAuth } from '../context/AuthContext';
import { useOnboarding, ModuleTips } from '../components/onboarding';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
// Tabs removed - using single-page layout
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2,
  Plus,
  RefreshCw,
  Trash2,
  CreditCard,
  Wallet,
  CheckCircle,
  AlertCircle,
  Link as LinkIcon,
  Unlink,
  ArrowDownUp,
  Clock,
  Download,
  Users,
  DollarSign,
  ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stripe publishable key from environment
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

const BankConnections = () => {
  const { t } = useTranslation();
  const { user, getAuthHeaders } = useAuth();
  const { triggerAction } = useOnboarding();
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [totalBalance, setTotalBalance] = useState(0);
  const [bankTransactions, setBankTransactions] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState({});
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [importingToLedger, setImportingToLedger] = useState(false);
  const [confirmDisconnectId, setConfirmDisconnectId] = useState(null);

  useEffect(() => {
    fetchConnectionStatus();
    fetchConnectedAccounts();
    fetchBankTransactions();
  }, []);

  const fetchConnectionStatus = async () => {
    try {
      const response = await axios.get(`${API}/stripe/financial-connections/status`);
      setConnectionStatus(response.data);
    } catch (error) {
      // Connection status unavailable
    }
  };

  const fetchConnectedAccounts = async () => {
    if (!user?.user_id) return;
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/stripe/financial-connections/accounts`,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setConnectedAccounts(response.data.accounts || []);
      // ── Commandment II: use backend-computed total_balance ──
      if (response.data.total_balance !== undefined) {
        setTotalBalance(response.data.total_balance);
      }
    } catch (error) {
      // Connected accounts unavailable
    } finally {
      setLoading(false);
    }
  };

  const fetchBankTransactions = async () => {
    if (!user?.user_id) return;
    
    try {
      const response = await axios.get(
        `${API}/stripe/financial-connections/transactions?imported=false&limit=200`,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setBankTransactions(response.data.transactions || []);
    } catch (error) {
      // Bank transactions unavailable
    }
  };

  const handleConnectBank = async () => {
    if (!STRIPE_PUBLISHABLE_KEY) {
      toast.error(t('bank.stripe_not_configured'));
      return;
    }

    setConnecting(true);
    try {
      // Step 1: Create a Financial Connections session
      const sessionResponse = await axios.post(
        `${API}/stripe/financial-connections/session`,
        { return_url: window.location.href },
        { headers: getAuthHeaders(), withCredentials: true }
      );

      const { client_secret, session_id } = sessionResponse.data;

      // Step 2: Load Stripe and open Financial Connections modal
      const stripe = await loadStripe(STRIPE_PUBLISHABLE_KEY);
      
      if (!stripe) {
        throw new Error('Failed to load Stripe');
      }

      const result = await stripe.collectFinancialConnectionsAccounts({
        clientSecret: client_secret,
      });

      if (result.error) {
        toast.error(result.error.message || t('bank.failed_to_connect'));
        return;
      }

      // Step 3: Finalize the connection with our backend
      if (result.financialConnectionsSession) {
        const finalizeResponse = await axios.post(
          `${API}/stripe/financial-connections/finalize`,
          { session_id: session_id },
          { headers: getAuthHeaders(), withCredentials: true }
        );
        
        toast.success(finalizeResponse.data.message || t('bank.bank_connected'));
        
        // Trigger onboarding action when user connects bank
        triggerAction('connect_bank');
        
        // Refresh accounts list
        await fetchConnectedAccounts();
        
        // Auto-sync after connecting
        toast.info(t('bank.starting_initial_sync'));
        await handleSyncAll();
      }

    } catch (error) {
      toast.error(error.response?.data?.detail || t('bank.failed_to_connect'));
    } finally {
      setConnecting(false);
    }
  };

  const handleSyncAll = async () => {
    setSyncing(prev => ({ ...prev, all: true }));
    try {
      const response = await axios.post(
        `${API}/stripe/financial-connections/sync`,
        {},
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      toast.success(t('bank.synced_transactions', { count: response.data.total_new_transactions }));
      
      // Show detailed results
      response.data.results.forEach(result => {
        if (result.errors.length > 0) {
          toast.warning(t('toast.sync_errors', { institution: result.institution }));
        }
      });
      
      await fetchConnectedAccounts();
      await fetchBankTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('bank.failed_to_sync'));
    } finally {
      setSyncing(prev => ({ ...prev, all: false }));
    }
  };

  const handleSyncAccount = async (accountId) => {
    setSyncing(prev => ({ ...prev, [accountId]: true }));
    try {
      const response = await axios.post(
        `${API}/stripe/financial-connections/sync`,
        { account_id: accountId },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      toast.success(t('bank.synced_transactions', { count: response.data.total_new_transactions }));
      await fetchConnectedAccounts();
      await fetchBankTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('bank.failed_to_sync'));
    } finally {
      setSyncing(prev => ({ ...prev, [accountId]: false }));
    }
  };

  const handleDisconnect = async (accountId) => {
    if (confirmDisconnectId !== accountId) {
      setConfirmDisconnectId(accountId);
      toast.info('Click disconnect again to confirm', { duration: 3000 });
      setTimeout(() => setConfirmDisconnectId(null), 3000);
      return;
    }
    setConfirmDisconnectId(null);
    
    try {
      await axios.delete(
        `${API}/stripe/financial-connections/accounts/${accountId}`,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success(t('bank.bank_disconnected'));
      await fetchConnectedAccounts();
    } catch (error) {
      toast.error(t('bank.failed_to_disconnect'));
    }
  };

  const handleImportToLedger = async () => {
    if (selectedTransactions.length === 0) {
      toast.warning(t('bank.select_transactions'));
      return;
    }

    setImportingToLedger(true);
    try {
      const response = await axios.post(
        `${API}/stripe/financial-connections/transactions/import-to-ledger`,
        selectedTransactions,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      toast.success(t('bank.imported_count', { count: response.data.imported_count }));
      setSelectedTransactions([]);
      await fetchBankTransactions();
    } catch (error) {
      toast.error(t('bank.failed_to_import'));
    } finally {
      setImportingToLedger(false);
    }
  };

  const toggleTransactionSelection = (txnId) => {
    setSelectedTransactions(prev => 
      prev.includes(txnId) 
        ? prev.filter(id => id !== txnId)
        : [...prev, txnId]
    );
  };

  const selectAllTransactions = () => {
    if (selectedTransactions.length === bankTransactions.length) {
      setSelectedTransactions([]);
    } else {
      setSelectedTransactions(bankTransactions.map(t => t.transaction_id));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };

  const getAccountIcon = (category) => {
    switch (category?.toLowerCase()) {
      case 'cash':
        return <Wallet className="w-5 h-5 text-blue-600" />;
      case 'credit':
        return <CreditCard className="w-5 h-5 text-purple-600" />;
      case 'investment':
        return <Building2 className="w-5 h-5 text-emerald-600" />;
      default:
        return <Building2 className="w-5 h-5 text-gray-600" />;
    }
  };

  // ── Commandment II: totalBalance is a backend DTO field ──
  // lastSyncTime is a UI/display-only derived value — acceptable on client
  const lastSyncTime = connectedAccounts.reduce((latest, a) => {
    if (!a.last_sync_at) return latest;
    return !latest || new Date(a.last_sync_at) > new Date(latest) ? a.last_sync_at : latest;
  }, null);
  const [expandedAccount, setExpandedAccount] = useState(null);

  return (
  <>
    <DashboardLayout>
      <div className="space-y-6" data-testid="bank-connections-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
              <Building2 className="w-6 h-6 text-emerald-600" />
              {t('bank.title')}
            </h1>
            <p className="text-stone-500 text-sm">{t('bank.subtitle')}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {connectedAccounts.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleSyncAll} disabled={syncing.all} data-testid="sync-all-btn">
                <RefreshCw className={`w-4 h-4 mr-1.5 ${syncing.all ? 'animate-spin' : ''}`} />
                {t('bank.sync_all')}
              </Button>
            )}
            {connectionStatus?.configured && (
              <Button size="sm" onClick={handleConnectBank} disabled={connecting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="connect-bank-btn">
                {connecting ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
                {t('bank.connect')}
              </Button>
            )}
          </div>
        </div>

        {/* Not Configured Warning */}
        {connectionStatus && !connectionStatus.configured && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">{t('bank.stripe_not_configured')}</p>
                  <p className="text-sm text-amber-700 mt-0.5">{t('bank.stripe_not_configured_desc')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <Card><CardContent className="py-12 text-center"><RefreshCw className="w-8 h-8 animate-spin text-stone-300 mx-auto" /></CardContent></Card>
        ) : connectedAccounts.length === 0 ? (
          /* Empty State */
          <Card className="border-dashed border-2 border-stone-200">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 bg-stone-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <LinkIcon className="w-8 h-8 text-stone-400" />
              </div>
              <h3 className="text-lg font-semibold text-stone-800">{t('bank.no_connections')}</h3>
              <p className="text-stone-500 mt-1 mb-5 max-w-sm mx-auto text-sm">{t('bank.no_connections_desc')}</p>
              {connectionStatus?.configured && (
                <Button onClick={handleConnectBank} disabled={connecting} className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="w-4 h-4 mr-2" />{t('bank.connect_bank_account')}
                </Button>
              )}
              <div className="mt-6 pt-4 border-t border-stone-100 flex items-center justify-center gap-1.5 text-xs text-stone-400">
                <span>{t('bank.powered_by_stripe')}</span>
                <span>|</span>
                <span>{t('bank.financial_connections')}</span>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Summary Banner */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="bg-emerald-50 border-emerald-100">
                <CardContent className="p-4">
                  <p className="text-xs text-emerald-700 font-medium">Total Balance</p>
                  <p className="text-xl font-bold text-emerald-800 mt-0.5">{formatCurrency(totalBalance)}</p>
                </CardContent>
              </Card>
              <Card className="bg-stone-50 border-stone-100">
                <CardContent className="p-4">
                  <p className="text-xs text-stone-500 font-medium">Accounts</p>
                  <p className="text-xl font-bold text-stone-800 mt-0.5">{connectedAccounts.length}</p>
                </CardContent>
              </Card>
              <Card className="bg-stone-50 border-stone-100">
                <CardContent className="p-4">
                  <p className="text-xs text-stone-500 font-medium">To Import</p>
                  <p className="text-xl font-bold text-stone-800 mt-0.5">{bankTransactions.length}</p>
                </CardContent>
              </Card>
              <Card className="bg-stone-50 border-stone-100">
                <CardContent className="p-4">
                  <p className="text-xs text-stone-500 font-medium">Last Sync</p>
                  <p className="text-sm font-medium text-stone-700 mt-1">{lastSyncTime ? formatDate(lastSyncTime) : 'Never'}</p>
                </CardContent>
              </Card>
            </div>

            {/* Connected Accounts — Compact Rows */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-emerald-600" />
                  Connected Accounts
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-stone-100">
                  {connectedAccounts.map((account) => (
                    <div key={account.account_id} data-testid={`account-${account.account_id}`}>
                      <div 
                        className="flex items-center gap-3 px-4 py-3 hover:bg-stone-50 transition-colors cursor-pointer"
                        onClick={() => setExpandedAccount(expandedAccount === account.account_id ? null : account.account_id)}
                      >
                        <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          {getAccountIcon(account.category)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-stone-800 text-sm truncate">{account.display_name || account.institution_name}</p>
                            {account.last4 && <span className="text-xs text-stone-400">•••• {account.last4}</span>}
                            <Badge className={`text-[10px] px-1.5 py-0 ${account.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                              {account.status}
                            </Badge>
                          </div>
                          <p className="text-xs text-stone-400 mt-0.5">{account.institution_name} {account.subcategory ? `· ${account.subcategory}` : ''}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-bold text-stone-800">{formatCurrency(account.balance_current)}</p>
                          {account.balance_available !== null && account.balance_available !== undefined && (
                            <p className="text-xs text-stone-400">{formatCurrency(account.balance_available)} avail</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); handleSyncAccount(account.account_id); }} disabled={syncing[account.account_id]} data-testid={`sync-btn-${account.account_id}`}>
                            <RefreshCw className={`w-3.5 h-3.5 ${syncing[account.account_id] ? 'animate-spin text-emerald-600' : 'text-stone-400'}`} />
                          </Button>
                          <Button variant="ghost" size="icon" className={`h-8 w-8 ${confirmDisconnectId === account.account_id ? 'bg-red-500 text-white' : 'text-stone-400 hover:text-red-500'}`} onClick={(e) => { e.stopPropagation(); handleDisconnect(account.account_id); }} data-testid={`disconnect-btn-${account.account_id}`}>
                            <Unlink className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                      {/* Expanded Details */}
                      {expandedAccount === account.account_id && (
                        <div className="px-4 pb-3 pt-1 bg-stone-50 border-t border-stone-100">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                            <div><span className="text-stone-400">Connected</span><p className="font-medium text-stone-700">{formatDate(account.created_at)}</p></div>
                            <div><span className="text-stone-400">Last Sync</span><p className="font-medium text-stone-700">{formatDate(account.last_sync_at)}</p></div>
                            <div><span className="text-stone-400">Type</span><p className="font-medium text-stone-700 capitalize">{account.category || 'N/A'} · {account.subcategory || ''}</p></div>
                            {account.owners && account.owners.length > 0 && (
                              <div><span className="text-stone-400">Owner(s)</span><p className="font-medium text-stone-700">{account.owners.map(o => o.name).join(', ')}</p></div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Unimported Transactions */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ArrowDownUp className="w-4 h-4 text-stone-500" />
                    Transactions to Import
                    {bankTransactions.length > 0 && (
                      <Badge className="bg-amber-100 text-amber-700 text-xs">{bankTransactions.length} new</Badge>
                    )}
                  </CardTitle>
                  {bankTransactions.length > 0 && (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={selectAllTransactions}>
                        {selectedTransactions.length === bankTransactions.length ? t('bank.deselect_all') : t('bank.select_all')}
                      </Button>
                      <Button size="sm" onClick={handleImportToLedger} disabled={selectedTransactions.length === 0 || importingToLedger} className="bg-emerald-600 hover:bg-emerald-700">
                        {importingToLedger ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5 mr-1.5" />}
                        Import ({selectedTransactions.length})
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {bankTransactions.length === 0 ? (
                  <div className="py-8 text-center text-stone-400">
                    <CheckCircle className="w-10 h-10 mx-auto mb-2 text-emerald-300" />
                    <p className="text-sm font-medium text-stone-500">All caught up</p>
                    <p className="text-xs text-stone-400 mt-0.5">No new transactions to import</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10"></TableHead>
                          <TableHead className="text-xs">{t('common.date')}</TableHead>
                          <TableHead className="text-xs">{t('common.description')}</TableHead>
                          <TableHead className="text-xs">{t('common.bank')}</TableHead>
                          <TableHead className="text-xs text-right">{t('common.amount')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bankTransactions.map((txn) => (
                          <TableRow key={txn.transaction_id} className={selectedTransactions.includes(txn.transaction_id) ? 'bg-emerald-50' : ''}>
                            <TableCell><Checkbox checked={selectedTransactions.includes(txn.transaction_id)} onCheckedChange={() => toggleTransactionSelection(txn.transaction_id)} /></TableCell>
                            <TableCell className="text-sm">{formatDate(txn.transacted_at)}</TableCell>
                            <TableCell className="text-sm max-w-[250px] truncate">{txn.description || 'Bank Transaction'}</TableCell>
                            <TableCell className="text-xs text-stone-400">{txn.institution_name}</TableCell>
                            <TableCell className={`text-sm text-right font-medium ${txn.type === 'income' ? 'text-emerald-600' : 'text-red-600'}`}>
                              {txn.type === 'income' ? '+' : '-'}{formatCurrency(txn.amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
    <ModuleTips moduleId="bank-connections" />
  </>
  );
};

export default BankConnections;
